import { GET_ACTIVE_FILES_FAILURE } from 'modules/dashboard/myFiles/myFiles.types';
import { GET_CLIENTS_FAILURE } from 'modules/clients/types';
import {
  GET_EXCEPTION_COMMENTS_FAILURE,
  GET_EXTERNAL_COMMENTS_FAILURE,
} from 'modules/dashboard/types';
import { GET_INTERNAL_USERS_FAILURE } from 'modules/common/store/types';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { connect } from 'react-redux';
import { getActiveFiles } from 'modules/dashboard/myFiles/myFiles.actions';
import { getClients } from 'modules/clients/clients.actions';
import {
  getExceptionComments,
  getExternalComments,
} from 'modules/dashboard/dashboard.actions';
import { getInternalUsers } from 'modules/common/common.actions';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import CsmDashboardComponent from './csmDashboard.component';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const INITIAL_FILTERS = { clientId: null, fileId: null };

const CsmDashboardContainer = (props) => {
  const { userId, internalUsers, clients } = props;

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [isLoadingLookups, setIsLoadingLookups] = useState(false);

  useEffect(() => {
    const loadLookups = async () => {
      setIsLoadingLookups(true);

      const usersResult = await props.getInternalUsers();
      if (usersResult.type === GET_INTERNAL_USERS_FAILURE) {
        props.showToast('Failed to load users', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const clientsResult = await props.getClients();
      if (clientsResult.type === GET_CLIENTS_FAILURE) {
        props.showToast('Failed to load clients', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      setIsLoadingLookups(false);
    };

    props.setPageTitle('CSM Dashboard');
    loadLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default the selector to the logged-in user once their profile is available
  useEffect(() => {
    if (userId && !selectedUserId) {
      setSelectedUserId(userId);
    }
  }, [userId, selectedUserId]);

  // Every dashboard endpoint is scoped to the selected user, so reload whenever it changes
  useEffect(() => {
    if (!selectedUserId) return;

    const loadDashboardData = async () => {
      const commentsResult = await props.getExternalComments(selectedUserId);
      if (commentsResult.type === GET_EXTERNAL_COMMENTS_FAILURE) {
        props.showToast('Failed to load external comments', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const exceptionsResult = await props.getExceptionComments(selectedUserId);
      if (exceptionsResult.type === GET_EXCEPTION_COMMENTS_FAILURE) {
        props.showToast('Failed to load exceptions', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      // My Files data is loaded up front so the tab is populated before it is opened
      const activeFilesResult = await props.getActiveFiles(selectedUserId);
      if (activeFilesResult.type === GET_ACTIVE_FILES_FAILURE) {
        props.showToast('Failed to load active files', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
    };

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const handleUserChange = (newUserId) => {
    setSelectedUserId(newUserId);
    setFilters(INITIAL_FILTERS);
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  return (
    <CsmDashboardComponent
      users={internalUsers || []}
      selectedUserId={selectedUserId}
      loggedInUserId={userId}
      onUserChange={handleUserChange}
      clients={clients || []}
      filters={filters}
      onFilterChange={handleFilterChange}
      isLoading={isLoadingLookups}
      externalComments={props.externalComments || []}
      exceptionComments={props.exceptionComments || []}
      activeFiles={props.activeFiles || []}
      isLoadingExternalComments={props.isLoadingExternalComments}
      isLoadingExceptionComments={props.isLoadingExceptionComments}
      isLoadingActiveFiles={props.isLoadingActiveFiles}
    />
  );
};

CsmDashboardContainer.propTypes = {
  userId: PropTypes.number,
  internalUsers: PropTypes.array,
  clients: PropTypes.array,
  externalComments: PropTypes.array,
  exceptionComments: PropTypes.array,
  activeFiles: PropTypes.array,
  isLoadingExternalComments: PropTypes.bool,
  isLoadingExceptionComments: PropTypes.bool,
  isLoadingActiveFiles: PropTypes.bool,
  setPageTitle: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  getInternalUsers: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getExternalComments: PropTypes.func.isRequired,
  getExceptionComments: PropTypes.func.isRequired,
  getActiveFiles: PropTypes.func.isRequired,
};

CsmDashboardContainer.defaultProps = {
  userId: null,
  internalUsers: [],
  clients: [],
  externalComments: [],
  exceptionComments: [],
  activeFiles: [],
  isLoadingExternalComments: false,
  isLoadingExceptionComments: false,
  isLoadingActiveFiles: false,
};

const mapStateToProps = (state) => ({
  userId: state.auth.profile?.internalUserId,
  internalUsers: state.common.internalUsers,
  clients: state.clients?.unmappedClients,

  // External comments and exceptions share the RC dashboard's endpoints,
  // so they also share its slice of the store
  externalComments: state.dashboard.externalComments,
  exceptionComments: state.dashboard.exceptionComments,
  isLoadingExternalComments: state.dashboard.isLoadingExternalComments,
  isLoadingExceptionComments: state.dashboard.isLoadingExceptionComments,

  // Active files come from the shared myFiles slice
  activeFiles: state.myFiles.activeFiles,
  isLoadingActiveFiles: state.myFiles.isLoadingActiveFiles,
});

const actionCreators = {
  setPageTitle,
  showToast,
  getInternalUsers,
  getClients,
  getExternalComments,
  getExceptionComments,
  getActiveFiles,
};

export default connect(mapStateToProps, actionCreators)(CsmDashboardContainer);
